import { createContext, Dispatch, SetStateAction } from 'react';
import { TreeControlState } from 'components/explorer';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { EagerLoadTreeNodeDTO } from 'business/models/Models';

export const eagerLoadNodesList = [
  ETreeNodeTypes.Project,
  ETreeNodeTypes.ProjectFolder,
  ETreeNodeTypes.Projects,
  ETreeNodeTypes.ProjectsFolder,
  ETreeNodeTypes.ScreensFolder,
  ETreeNodeTypes.Screens,
  ETreeNodeTypes.Screen,
];

export const eagerLoadNodesExperimentOnlyList = [
  ETreeNodeTypes.FragmentScreen,
  ETreeNodeTypes.Protein,
  ETreeNodeTypes.Seed,
];

export const expandNodesRecursively = (nodes, expand) => {
  return nodes.map((node) => ({
    ...node,
    state: {
      ...node.state,
      expanded: expand,
    },
    children: node.children ? expandNodesRecursively(node.children, expand) : [],
  }));
};

export const findNodeAndExpandParents = (nodes, targetId) => {
  let foundNode = null;

  const recurse = (currentNodes) => {
    return currentNodes.map((node) => {
      const updatedNode = { ...node };

      // if children exist, go deeper
      if (node.children && node.children.length > 0) {
        const resultChildren = recurse(node.children);

        // if any child was found and expanded → expand this node too
        const childContainsFound = resultChildren.some((c) => c.state.expanded);

        if (childContainsFound) {
          updatedNode.state = { ...node.state, expanded: true };
        }

        updatedNode.children = resultChildren;
      }

      // if this is the found node
      if (node.id === targetId) {
        foundNode = updatedNode;
        updatedNode.state = { ...node.state, expanded: true };
      }

      return updatedNode;
    });
  };

  const updatedNodes = recurse(nodes);
  return { updatedNodes, foundNode };
};

export type LayersContainerContextType = {
  treeControlState: TreeControlState;
  setTreeControlState: Dispatch<SetStateAction<TreeControlState>>;
};

export const LayersContainerContext = createContext<
  LayersContainerContextType | undefined
>(undefined);

export const getTreeNodeIdsByType = (
  root: EagerLoadTreeNodeDTO[],
  type: ETreeNodeTypes,
): Set<number> => {
  const result = new Set<number>();

  const traverse = (n: EagerLoadTreeNodeDTO) => {
    if (!n) {
      return;
    }

    if (n.type === type) {
      const id = n.type === ETreeNodeTypes.Screen ? n.extra?.id : n.id;

      if (id != null) {
        result.add(Number(id));
      }
    }

    if (Array.isArray(n.children)) {
      n.children.forEach(traverse);
    }
  };

  (root || []).forEach(traverse);

  return result;
};
